import { useReviewStreak } from "~/lib/client/streak";
import {
	useStudyActivity,
	useStudyStats,
	useFlashcardMaturity,
	useForecastData,
} from "~/lib/client/activity";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { StudyActivity } from "~/components/dashboard/activity/study-activity";
import { ForecastChart } from "~/components/dashboard/charts/forecast-chart";
import { MaturityChart } from "~/components/dashboard/charts/maturity-chart";
import { StudyStatsChart } from "~/components/dashboard/charts/study-stats-chart";
import { Section } from "~/components/dashboard/common/section";
import { ReviewQueue } from "~/components/dashboard/user/review-queue";
import { UserGreeting } from "~/components/dashboard/user/user-greeting";
import { currentPageContextAtom } from "~/components/practice/chat-widget";
import { generateOverviewPageContext } from "~/lib/context-gen";
import { getAuthSession } from "~/lib/server/functions/auth";
import { m } from "~/i18n/messages";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { cn } from "~/lib/utils";

function ReviewCountSkeleton({ className }: { className?: string }) {
	return (
		<div className={cn("animate-pulse bg-gray-200 rounded h-8 w-32", className)} />
	);
}

export const Route = createFileRoute("/app/overview")({
	component: OverviewPage,
	loader: async () => {
		const { user } = await getAuthSession();
		if (!user) throw redirect({ to: "/" });
		return { user };
	},
});

function ContextProvider() {
	const setPageContext = useSetAtom(currentPageContextAtom);
	const { data: reviewStreak } = useReviewStreak();
	const { data: studyActivityData } = useStudyActivity();
	const { data: studyStatsData } = useStudyStats();
	const { data: maturityChartData } = useFlashcardMaturity();
	const { data: forecastData } = useForecastData();

	useEffect(() => {
		setPageContext({
			title: m["navbar.overview_link"](),
			slug: "/app/overview",
			contextData: generateOverviewPageContext({
				reviewStreak,
				reviewQueueCount: 0,
				studyActivityData,
				studyStatsData,
				maturityChartData,
				forecastData,
			}),
		});
	}, [
		setPageContext,
		reviewStreak,
		studyActivityData,
		studyStatsData,
		maturityChartData,
		forecastData,
	]);

	return null;
}

function OverviewPage() {
	const { user } = Route.useLoaderData();

	return (
		<div className="h-full w-full">
			<SuspenseFallback
				loading={null}
				error={<div className="text-red-500">Error loading page context</div>}
			>
				<ContextProvider />
			</SuspenseFallback>
			<div className="mx-auto max-w-screen-md space-y-2 p-4">
				<Section priority="primary" className="p-4">
					<UserGreeting name={user.name} />
					<SuspenseFallback
						loading={<ReviewCountSkeleton />}
						error={<div className="text-red-500">Error loading review queue</div>}
					>
						<ReviewQueue />
					</SuspenseFallback>
				</Section>

				<Section title={m["dashboard.study_activity"]()} className="p-4">
					<StudyActivity />
				</Section>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
					<Section title={m["dashboard.study_stats"]()} className="p-4">
						<StudyStatsChart />
					</Section>
					<Section
						title={m["dashboard.maturity_distribution"]()}
						className="p-4"
					>
						<MaturityChart />
					</Section>
				</div>

				<Section title={m["dashboard.review_forecast"]()} className="p-4">
					<ForecastChart />
				</Section>
			</div>
		</div>
	);
}
